/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react'
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  styled,
  ButtonProps,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import shallow from 'zustand/shallow'
import Logo from 'assets/img/logo_yellow.png'
import { useForm, Controller } from 'react-hook-form'
import Katoikia from 'assets/img/katoikia.png'
import BusinessCase from 'assets/svg/menu/buss_profile.svg'
import CreateAccount from 'assets/svg/menu/signin_yellow.svg'
import FacebookLogo from 'assets/img/facebook.png'
import GoogleLogo from 'assets/img/google.png'
import { useTranslation } from 'react-i18next'
import {
  Link as RouterLink,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import AuthenticationService from 'api/services/Authentication'
import ResetPassword from './ResetPassword'
import { useAuthStore } from 'store'
import TokenService from 'services/TokenService'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import usePageView from 'hooks/usePageView'
import KeycloakService from 'services/KeycloakService'

const SocialButton = styled(Button)<ButtonProps>(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: '1px solid #8C8C8C',
  minWidth: '232px',
  height: '62px',
  borderRadius: '8px',
  color: theme.palette.text.primary,
  boxShadow:
    ' 0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
  '&:hover': {
    backgroundColor: theme.palette.common.white,
    borderColor: '#8C8C8C',
    boxShadow:
      ' 0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
  },
  fontSize: ' 14px',
  lineHeight: '16px',
  textTransform: 'none',
  justifyContent: 'center',
  width: '100%',
  bgcolor: '#165DB6',
}))

function Login() {
  usePageView()
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()
  const location = useLocation()

  const [setAccessToken, setRefreshToken] = useAuthStore(
    (state) => [state.setAccessToken, state.setRefreshToken],
    shallow
  )

  const [showPassword, setShowPassword] = React.useState<boolean>(false)

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev)
  }

  const [forgotPassword, setForgotPassword] = React.useState(false)

  const { control, handleSubmit } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const login = AuthenticationService.hooks.useLogin({
    onSuccess: (data) => {
      setRefreshToken(data.refresh_token)
      TokenService.setRefreshToken(data.refresh_token)
      setAccessToken(data.access_token)
      TokenService.setAccessToken(data.access_token)
      setAccessToken(data.access_token)
      const FROM = searchParams.get(`from`)
      if (FROM) {
        window.location.href = `${
          process.env.REACT_APP_REDIRECT_SEARCH_APP_URL
        }${location?.search?.slice(6)}`
      } else {
        window.location.reload()
      }
    },
  })

  const onSubmit = (data) => {
    login.mutate({
      username: data.username,
      password: data.password,
    })
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 6 }, py: { xs: 2, sm: 4 } }}>
      <Grid
        container
        direction="row"
        justifyContent="space-evenly"
        alignItems="center"
      >
        <Grid
          item
          xs={12}
          sm={10}
          md={8}
          lg={6}
          sx={{
            boxShadow: 25,
            border: 1,
            borderColor: grey[300],
            borderRadius: 2.5,
            backgroundColor: '#fff',
            p: { xs: 4, sm: 8 },
          }}
        >
          <Grid item>
            <Grid container direction="column">
              <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={Logo}
                  sx={{ width: '100%', maxWidth: 150 }}
                />
              </Grid>
              <Grid item>
                <Typography
                  align="center"
                  variant="h4"
                  color={'grey.500'}
                  sx={{ fontSize: { xs: 16, md: 24 }, mb: 3 }}
                >
                  {t('signInPage.title')}
                </Typography>
              </Grid>

              <Grid item component="form" onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  name="username"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('signInPage.form.email')}
                      variant="filled"
                      error={!!error}
                      fullWidth
                      {...field}
                      sx={{
                        '& .MuiFilledInput-input': {
                          fontSize: 20,
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: 20,
                        },
                      }}
                      helperText={error ? error?.message : ' '}
                    />
                  )}
                />
                <Controller
                  rules={{
                    required: t(
                      'validation.fieldRequired'
                    ) as unknown as string,
                  }}
                  name="password"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label={t('signInPage.form.password')}
                      variant="filled"
                      type={showPassword ? 'text' : 'password'}
                      error={!!error}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              color={showPassword ? 'primary' : 'default'}
                              onClick={handleTogglePassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      helperText={error ? error?.message : ' '}
                      sx={{
                        my: 2,
                        '& .MuiFilledInput-input': {
                          fontSize: 20,
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: 20,
                        },
                      }}
                      {...field}
                    ></TextField>
                  )}
                />

                {login.isError ? (
                  <Grid pb={2}>
                    <Alert severity="error">
                      {t(
                        `signInPage.${
                          login?.error?.response?.data?.error_description ===
                          'Invalid user credentials'
                            ? 'wrongCredential'
                            : login?.error?.response?.data
                                ?.error_description === 'Account disabled'
                            ? 'wrongCredential'
                            : 'genericError'
                        }`
                      )}
                    </Alert>
                  </Grid>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    py: 2,
                    minHeight: 70,
                  }}
                >
                  {login.isLoading ? (
                    <CircularProgress style={{ color: 'white' }} size={30} />
                  ) : (
                    <Typography fontSize={25}>
                      {' '}
                      {t('signInPage.form.loginButton')}
                    </Typography>
                  )}
                </Button>
              </Grid>

              <Grid item sx={{ mt: 4 }}>
                <Link
                  component="button"
                  underline="none"
                  onClick={() => {
                    forgotPassword
                      ? setForgotPassword(false)
                      : setForgotPassword(true)
                  }}
                  color="inherit"
                >
                  <Typography
                    variant="h5"
                    color={grey[400]}
                    sx={{ fontSize: { xs: 16, sm: 20 } }}
                  >
                    {t('signInPage.forgotPassword.title')}
                  </Typography>
                </Link>
              </Grid>

              <ResetPassword visibility={forgotPassword} />

              <Grid item sx={{ mt: 4 }}>
                <SocialButton
                  onClick={() =>
                    KeycloakService.doLogin({ idpHint: 'facebook' })
                  }
                >
                  <Box
                    component="img"
                    src={FacebookLogo}
                    sx={{
                      width: 30,
                      height: 30,
                      mr: '14.5px',
                    }}
                  />
                  {`${t('signInPage.form.social')} Facebook`}
                </SocialButton>
              </Grid>

              <Grid item sx={{ mt: 4 }}>
                <SocialButton
                  onClick={() => KeycloakService.doLogin({ idpHint: 'google' })}
                >
                  <Box
                    component="img"
                    src={GoogleLogo}
                    sx={{
                      width: 30,
                      height: 30,
                      mr: '14.5px',
                    }}
                  />
                  {`${t('signInPage.form.social')} Google`}
                </SocialButton>
              </Grid>

              <Grid item sx={{ mt: 4 }}>
                <Grid container columnSpacing={1} rowSpacing={4}>
                  <Grid item xs={4} md={2}>
                    <Box
                      component="img"
                      src={BusinessCase}
                      sx={{
                        width: 60,
                        borderRadius: 50,
                        border: 1,
                        p: 1,
                        borderColor: grey[400],
                      }}
                    />
                  </Grid>

                  <Grid item xs={8} md={4}>
                    <Link
                      component={RouterLink}
                      to={`/sign-up${location.search}`}
                      underline="none"
                      color="inherit"
                    >
                      <Typography
                        fontSize={{ xs: '16px', sm: '20px' }}
                        align="left"
                        sx={{ fontWeight: 500 }}
                      >
                        {t('signInPage.createBusinessAccount')}
                      </Typography>
                    </Link>
                  </Grid>

                  <Grid item xs={4} md={2}>
                    <Box
                      component="img"
                      src={CreateAccount}
                      sx={{
                        width: 60,
                        borderRadius: 50,
                        border: 1,
                        p: 1,
                        borderColor: grey[400],
                      }}
                    />
                  </Grid>
                  <Grid item xs={8} md={4}>
                    <Link
                      component={RouterLink}
                      to={`/sign-up-user${location.search}`}
                      underline="none"
                      color="inherit"
                    >
                      <Typography
                        fontSize={{ xs: '16px', sm: '20px' }}
                        align="left"
                        sx={{ fontWeight: 500 }}
                      >
                        {t('signInPage.createUserAccount')}
                      </Typography>
                    </Link>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: { xs: 2, sm: 3 },
          }}
        >
          <Box
            component="img"
            src={Katoikia}
            sx={{ width: '100%', maxWidth: { xs: 250, sm: 450 } }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default Login
